import { useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Spinner from "../Components/Spinner";
import { MdOutlineCheckCircle, MdOutlineCircle } from "react-icons/md";
import { FaRegSquare, FaSquare } from "react-icons/fa";
import MandatorySign from "../Components/MandatorySign";
import ErrorMessage from "../Components/ErrorMessage";
import { categoryOptions, toolsOptions, regionOptions, runtimeOptions } from "../MCPs/ContstantValues"
import { PiLeaf } from "react-icons/pi";
import { useLanguage } from "../Translations/LenguageContext";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const OptionButton = ({ selected, onClick, title, description, icon }) => (
    <div
        className={`mcp_type_btn fade-in ${selected ? "mcp_type_btn_selected" : ""}`}
        onClick={onClick}
    >
        <div className="mcp_type_btn_inner">
            <i>{selected ? <MdOutlineCheckCircle /> : <MdOutlineCircle />}</i>
            <span className="mcp_type_btn_title">{icon} {title}</span>
            <span className="mcp_type_btn_desc">{description}</span>
        </div>
    </div>
);

const AppTypeModal = ({
    account_id,
    show,
    handleClose,
    setOpenSidebar
}) => {
    const { t } = useLanguage()
    const navigate = useNavigate();
    const { org_id } = useParams()
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [type, setType] = useState("");
    const [errorMessage, setErrorMessage] = useState({ message: "", display: false });
    const [endpointUrl, setEndpointUrl] = useState(null)
    const [formData, setFormData] = useState({
        app_name: "",
        description: "",
        organization_ids: [`test-${org_id}`],
    });

    const handleSave = async () => {
        setErrorMessage({ message: "", display: false });

        if (!formData.app_name) {
            setErrorMessage({ message: t.abilities.ability_name_req, display: true });
            return;
        }

        if (!formData.description) {
            setErrorMessage({ message: t.abilities.ability_desc_req, display: true });
            return;
        }

        if (formData.description.length > 300) {
            setErrorMessage({ message: t.abilities.ab_desc_long, display: true });
            return;
        }

        setLoading(true);
        try {
            let ips = ""
            formData.network_mode == "restricted" ? ips = formData.allowed_ips : ips = "0.0.0.0/0"

            const response = await fetch(`${GEN_BACKEND_LINK}/save_app`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    account_id,
                    org_id,
                    app_id: null,
                    app_name: formData.app_name,
                    description: formData.description,
                    organization_ids: formData.organization_ids,
                    type: type
                }),
            });

            const result = await response.json();

            if (!response.ok) throw new Error("Failed to deploy Ability");

            if (result.status == "success") {
                setOpenSidebar(false)
                const mainUrl = `/e8console/organizations/${org_id}/apps`;
                const targetPath =
                    type === "internal"
                        ? `${mainUrl}/app-builder/internal/${result.app_id}/1`
                        : `${mainUrl}/app-builder/external/${result.app_id}/1`;

                navigate(targetPath);
                CloseModal()
            }
        } catch (err) {
            setErrorMessage({ message: err.message, display: true });
        } finally {
            setLoading(false);
            setErrorMessage({ message: "", display: false });
        }
    };

    const handleNext = () => {
        setErrorMessage({ message: "", display: false })
        if (step === 1) {
            if (!type) {
                setErrorMessage({ message: t.abilities.select_option, display: true })
                return
            }
        }

        if (step === 2) {
            handleSave();
        } else if (step < 2) {
            setLoading(true)
            setTimeout(() => {
                setStep(step + 1);
                setLoading(false)
            }, 1000);
        }
    };

    const handleBack = () => {
        if (step == 2) {
            setStep(1)
        }

        if (step == 3) {
            setStep(2)
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const CloseModal = () => {
        setFormData({
            app_name: "",
            description: "",
            organization_ids: [`test-${org_id}`],
        })
        setErrorMessage({ message: "", display: false })
        handleClose();
        setStep(1);
        setType("");
        setLoading(false);
    }

    return (
        <Modal show={show} onHide={CloseModal} centered className="mcp_modal_custom">
            <div className="modal_header">
                <p className="modal_header_title">
                    Build new app
                    <span>{step}/2</span>
                </p>
            </div>

            {step === 1 && (
                <div className="mcp_type_cont">
                    <OptionButton
                        selected={type === "internal"}
                        onClick={() => setType("internal")}
                        title="Internal"
                        description="Build applications for internal use within your organization."
                        icon={<FaRegSquare />}
                    />
                    <OptionButton
                        selected={type === "external"}
                        onClick={() => setType("external")}
                        title="External"
                        description="Build public facing applications designed for users outside your organization."
                        icon={<FaSquare />}
                    />
                </div>
            )}

            {step === 2 && (
                <div className="modal_main_cont">
                    <div className="modal_profile_input_cont">
                        <div>
                            <div className="label_input">
                                <label className="input_label">{t.title} <MandatorySign message={t.mandatory_field} /></label>
                                <input
                                    type="text"
                                    name="app_name"
                                    value={formData.app_name}
                                    onChange={handleChange}
                                    placeholder="Ex. Company website"
                                    className="register_form_first fade-in"
                                    autoComplete="off"
                                />
                            </div>
                            <div className="label_input">
                                <label className="input_label">{t.description} <MandatorySign message={t.mandatory_field} /></label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Describe what this app does"
                                    className="register_form_first textarea_reg fade-in"
                                    autoComplete="off"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="modal_footer">
                <div className="modal_footer_err_cont">
                    <ErrorMessage err={errorMessage} />
                </div>
                <div className="modal_footer_btn_cont">
                    <button
                        className="modale_save_btn"
                        onClick={handleNext}
                        disabled={
                            loading ||
                            (step === 1 && !type)                        }
                    >
                        {loading ? <Spinner loading={true} /> : step === 3 ? t.start : t.next}
                    </button>

                    {step > 1 && (
                        <button className="modale_close_btn" onClick={handleBack}>
                            <i className="fa-solid fa-chevron-left"></i> {t.back}
                        </button>
                    )}

                    {step === 1 && (
                        <button className="modale_close_btn" onClick={CloseModal}>
                            {t.close}
                        </button>
                    )}
                </div>
            </div>
        </Modal >
    );
};

export default AppTypeModal;